import React from "react";
import { Card } from "./shared";
import { suBreakdown } from "./SourcesUsesTables";
import * as m from "../lib/model";

const PL_ROWS = [
  { k: "revenue", label: "Revenue" },
  { k: "cogs", label: "Cost of goods sold", exp: true },
  { k: "gp", label: "Gross profit", bold: true },
  { k: "gmPct", label: "Gross margin %", pct: true },
  { k: "opex", label: "Operating expenses", exp: true },
  { k: "ebitda", label: "Adjusted EBITDA", bold: true },
  { k: "ebitdaPct", label: "EBITDA margin %", pct: true },
];

// Reported years, the pro-forma column (every add-on closed) and the projection, in one table.
export const plColumns = (M, V) => {
  const hist = m.historicalPL(M);
  const proj = m.projectedPL(V);
  const pf = m.proForma(V);
  const gm = m.grossMargin(M);
  const pfGp = pf.revenue * (gm / 100);
  const pfCol = pf.pf ? [{
    label: "Pro-forma", pro: true, revenue: pf.revenue, cogs: pf.revenue - pfGp,
    gp: pfGp, gmPct: gm, opex: pfGp - pf.sde,
    ebitda: pf.sde, ebitdaPct: pf.revenue ? (pf.sde / pf.revenue) * 100 : 0,
  }] : [];
  return { hist, proj, gm, cols: [...hist, ...pfCol, ...proj] };
};

export const plCagr = (V) => {
  const proj = m.projectedPL(V);
  const last = proj[proj.length - 1];
  return proj.length > 1 && proj[0].revenue > 0
    ? { pct: (Math.pow(last.revenue / proj[0].revenue, 1 / (proj.length - 1)) - 1) * 100, years: proj.length }
    : { pct: 0, years: proj.length };
};

export function PLCard({ M, V, testid = "pl-table-card" }) {
  const { hist, gm, cols } = plColumns(M, V);
  const consolidated = m.isRollupScope(V);
  const projRows = cols.filter((c) => !c.hist && !c.pro);
  const totalCagr = projRows.length > 1 && projRows[0].revenue > 0
    ? (Math.pow(projRows[projRows.length - 1].revenue / projRows[0].revenue, 1 / (projRows.length - 1)) - 1) * 100 : 0;
  const cell = (r, c) => {
    const v = c[r.k];
    if (r.pct) return c.revenue ? m.fmtPct(v) : "—";
    if (!c.revenue) return "—";
    return m.fmt$(r.exp ? -Math.abs(v) : v);
  };
  return (
    <Card title={hist.length ? "Historical, Pro-Forma & Projected P&L" : "Projected P&L"} testid={testid}>
      <div className="table-scroll" data-testid="pl-table-scroll">
      <table className="tbl pl-tbl" data-testid="pl-table">
        <thead>
          <tr>
            <th />
            {cols.map((c) => (
              <th key={c.label} className="num" data-testid={`pl-col-${c.label}`}>
                {c.label}
                <div className="pl-colsub">{c.hist ? "reported" : c.pro ? "all add-ons closed" : "projected"}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PL_ROWS.map((r) => (
            <tr key={r.k} className={r.bold ? "hl" : ""} data-testid={`pl-row-${r.k}`}>
              <td style={{ fontWeight: r.bold ? 700 : 400 }}>{r.label}</td>
              {cols.map((c) => (
                <td key={c.label} className={`num ${r.exp ? "exp" : ""}`} style={{ fontWeight: r.bold ? 700 : 400 }}>{cell(r, c)}</td>
              ))}
            </tr>
          ))}
          {V.slbOn && (
            <>
              <tr data-testid="pl-row-rent">
                <td>Less: leaseback rent</td>
                {cols.map((c) => <td key={c.label} className="num exp">{c.rent ? m.fmt$(-c.rent) : "—"}</td>)}
              </tr>
              <tr className="hl" data-testid="pl-row-ebitdaAfterRent">
                <td style={{ fontWeight: 700 }}>EBITDA after rent</td>
                {cols.map((c) => <td key={c.label} className="num" style={{ fontWeight: 700 }}>{c.rent ? m.fmt$(c.ebitdaAfterRent) : "—"}</td>)}
              </tr>
            </>
          )}
          <tr data-testid="pl-row-salary">
            <td>Less: owner / manager compensation</td>
            {cols.map((c) => <td key={c.label} className="num exp">{c.cfads != null && c.salary ? m.fmt$(-c.salary) : "—"}</td>)}
          </tr>
          <tr data-testid="pl-row-capex">
            <td>Less: maintenance capex</td>
            {cols.map((c) => <td key={c.label} className="num exp">{c.cfads != null && c.capex ? m.fmt$(-c.capex) : "—"}</td>)}
          </tr>
          {V.mgmtFeePct > 0 && (
            <tr data-testid="pl-row-mgmt">
              <td>Less: management fee</td>
              {cols.map((c) => <td key={c.label} className="num exp">{c.cfads != null && c.mgmt ? m.fmt$(-c.mgmt) : "—"}</td>)}
            </tr>
          )}
          <tr className="hl" data-testid="pl-row-cfads">
            <td style={{ fontWeight: 700 }}>Cash flow available for debt service</td>
            {cols.map((c) => <td key={c.label} className="num" style={{ fontWeight: 700 }}>{c.cfads != null ? m.fmt$(c.cfads) : "—"}</td>)}
          </tr>
          <tr data-testid="pl-row-ds">
            <td>Less: debt service (senior + seller notes{consolidated ? " + add-on debt" : ""})</td>
            {cols.map((c) => <td key={c.label} className="num exp">{c.cfads != null ? m.fmt$(-c.ds) : "—"}</td>)}
          </tr>
          <tr className="hl" data-testid="pl-row-fcf">
            <td style={{ fontWeight: 700 }}>Free cash flow after debt service</td>
            {cols.map((c) => <td key={c.label} className={`num ${c.fcf >= 0 ? "pos" : "neg"}`} style={{ fontWeight: 700 }}>{c.cfads != null ? m.fmt$(c.fcf) : "—"}</td>)}
          </tr>
          <tr data-testid="pl-row-netIncome">
            <td>Net income (reported)</td>
            {cols.map((c) => <td key={c.label} className={`num ${c.netIncome < 0 ? "neg" : c.netIncome > 0 ? "pos" : ""}`}>{c.hist && c.netIncome ? m.fmt$(c.netIncome) : "—"}</td>)}
          </tr>
        </tbody>
      </table>
      </div>
      <div className="ra-sub" style={{ marginTop: 10 }}>
        Debt service is the cash cost of the senior facility{V.snAmt > 0 ? " and the seller note" : ""}{consolidated ? " plus add-on debt" : ""} — it sits below EBITDA because it is financing, not operations. Every line between EBITDA and free cash flow is shown explicitly, so free cash flow ties to the pro-forma cash flow on Returns year for year. Revenue grows {V.sdeGrowth}% a year organically{consolidated && totalCagr > V.sdeGrowth + 0.1 ? `; the balance of the ${m.fmtPct(totalCagr)} total Y1–Y${projRows.length} revenue CAGR is driven by add-on acquisitions as they close, not organic growth` : ""}. Gross margin is held at {gm ? m.fmtPct(gm) : "0.0%"}; operating expenses are the derived balance between gross profit and the modeled adjusted EBITDA — EBITDA is built bottom-up from platform and add-on earnings, not forced to a target margin.
        {hist.length > 0 && consolidated ? " Reported years are platform-only; add-ons carry no operating history in the model." : ""}
        {V.slbOn ? ` The sale-leaseback is on, so rent of ${m.fmt$(m.annualRent(V))} in Year 1 (escalating ${V.rentEsc}% a year) is charged below EBITDA — reported years predate the leaseback and show no rent.` : ""}
      </div>
    </Card>
  );
}

// ---------- Transaction costs detail ----------
export function TransactionCostsCard({ V, testid = "su-costs-detail" }) {
  const consolidated = m.isRollupScope(V);
  const tc = m.transactionCosts(V);
  const ac = consolidated ? m.addonCosts(V) : { rows: [], fees: 0, count: 0 };
  const aSum = (k) => ac.rows.reduce((s, r) => s + (r[k] || 0), 0);
  const pct = (p) => (p || 0) / 100;
  const platPrice = V.askPrice || 0;
  const tcRows = [
    ["Acquisition fee", tc.acqFee, pct(V.acqFeePct) * aSum("price"), `${V.acqFeePct || 0}% of ${m.fmt$(platPrice + aSum("price"))} acquisition cost`],
    ["Due diligence", tc.ddCost, pct(m.ddPctOf(V)) * aSum("price"), `${(m.ddPctOf(V) || 0).toFixed(2)}% of ${m.fmt$(platPrice + aSum("price"))} acquisition cost`],
    ["Closing costs", tc.closingCost, pct(V.closingCostPct) * aSum("price"), `${V.closingCostPct || 0}% of ${m.fmt$(platPrice + aSum("price"))} acquisition cost`],
    ["Equity placement fee", tc.equityFee, pct(V.equityFeePct) * aSum("equityBase"), `${V.equityFeePct || 0}% of ${m.fmt$(m.equityPlacement(V) + aSum("equityBase"))} equity`],
    ["Debt placement fee", tc.debtFee, pct(V.debtFeePct) * (aSum("debt") + aSum("sellerNote")), `${V.debtFeePct || 0}% of ${m.fmt$(m.debtPlacement(V) + aSum("debt") + aSum("sellerNote"))} debt`],
  ];
  const tcTotal = tc.total + (V.transExpenses || 0) + (consolidated ? ac.fees : 0);
  return (
    <Card title="Transaction Costs Detail" className="table-scroll" testid={testid}>
      <table className="tbl" data-testid="su-costs-table">
        <thead><tr><th>Item</th>{consolidated && <><th className="num">Platform</th><th className="num">Add-ons</th></>}<th className="num">Amount</th></tr></thead>
        <tbody>
          {tcRows.filter(([, p, a]) => Math.abs(p) + Math.abs(a) > 0.5).map(([label, p, a, basis]) => (
            <tr key={label} data-testid={`su-cost-${label.replace(/\s+/g, "-").toLowerCase()}`}>
              <td>{label}<div style={{ fontSize: 10, color: "var(--text3)" }}>{basis}</div></td>
              {consolidated && <><td className="num">{p ? m.fmt$(p) : "—"}</td><td className="num">{a ? m.fmt$(a) : "—"}</td></>}
              <td className="num">{m.fmt$(p + a)}</td>
            </tr>
          ))}
          {(V.transExpenses || 0) > 0 && (
            <tr><td>Transaction expenses (direct)</td>{consolidated && <><td className="num">{m.fmt$(V.transExpenses)}</td><td className="num">—</td></>}<td className="num">{m.fmt$(V.transExpenses)}</td></tr>
          )}
          <tr className="total" data-testid="su-cost-total"><td>Total in Uses</td>{consolidated && <><td className="num">{m.fmt$(tc.total + (V.transExpenses || 0))}</td><td className="num">{m.fmt$(ac.fees)}</td></>}<td className="num">{m.fmt$(tcTotal)}</td></tr>
        </tbody>
      </table>
      <div className="ra-sub" style={{ marginTop: 8 }}>
        Edit these on Assumptions → Transaction Costs. They flow straight into Total Uses, so the equity plug moves with them.
        {consolidated ? ` The same schedule is charged on all ${ac.count} add-on${ac.count === 1 ? "" : "s"} — those dollars sit inside each add-on's equity check and appear as "Add-on transaction costs (fees)" in Uses.` : ""}
      </div>
    </Card>
  );
}

// ---------- Capitalisation: a restatement of Uses, so it can never drift from Sources & Uses ----------
export function CapitalisationCard({ V, testid = "su-capitalisation", style }) {
  const consolidated = m.isRollupScope(V);
  const recap = m.isRecap(V);
  const { su, totalDebt, totalEquity, slb, uses } = suBreakdown(V);
  const off = Math.abs(su.gap) > 1;
  const pctOf = (x) => (su.totalSources ? (x / su.totalSources) * 100 : 0);
  const debtPct = pctOf(totalDebt);
  const equityExSlb = Math.max(0, totalEquity - (slb || 0));
  const uPick = (test) => uses.filter((u) => test(u.d));
  const uSum = (rows) => rows.reduce((s, u) => s + u.a, 0);
  const addonPriceRows = uPick((d) => /^(Committed add-ons|Self-funded add-ons|Add-on ·)/.test(d));
  const addonNwcRows = uPick((d) => d === "Add-on working capital funded at close");
  const addonFeeRows = uPick((d) => d === "Add-on transaction costs (fees)");
  const cashRows = uPick((d) => d.startsWith("Cash left on balance sheet") || d === "Excess proceeds to balance sheet");
  const grouped = new Set([...addonPriceRows, ...addonNwcRows, ...addonFeeRows, ...cashRows].map((u) => u.d));
  const platformUses = uSum(uses.filter((u) => !grouped.has(u.d)));
  return (
    <Card title="Capitalisation" testid={testid} style={style}>
      <div className="su-bar" data-testid="su-cap-bar">
        <div className="su-bar-debt" style={{ width: `${Math.min(100, debtPct)}%` }} title={`Debt ${debtPct.toFixed(1)}%`} />
        <div className="su-bar-eq" style={{ width: `${Math.max(0, pctOf(equityExSlb))}%` }} title={`Equity ${pctOf(equityExSlb).toFixed(1)}%`} />
        {slb > 0.5 && <div className="su-bar-slb" style={{ width: `${pctOf(slb)}%` }} title={`Sale-leaseback ${pctOf(slb).toFixed(1)}%`} />}
      </div>
      <div className="su-legend">
        <span data-testid="su-cap-debt"><i className="su-dot su-dot-debt" />Debt {m.fmt$(totalDebt)} · {debtPct.toFixed(1)}%</span>
        <span data-testid="su-cap-equity"><i className="su-dot su-dot-eq" />Equity {m.fmt$(equityExSlb)} · {pctOf(equityExSlb).toFixed(1)}%</span>
        {slb > 0.5 && <span data-testid="su-cap-slb"><i className="su-dot su-dot-slb" />Sale-leaseback proceeds {m.fmt$(slb)} · {pctOf(slb).toFixed(1)}%</span>}
      </div>
      <div className="ra-sub" style={{ marginTop: 8 }} data-testid="su-cap-total">
        Total sources {m.fmt$(su.totalSources)} — {off ? `out of balance against Total Uses by ${m.fmt$(su.gap)}` : "ties to Total Uses"}.
      </div>
      <table className="tbl" style={{ marginTop: 14 }} data-testid="su-cap-table">
        <tbody>
          <tr data-testid="su-cap-platform">
            <td>Platform uses{consolidated ? " — at close" : ""}
              <div style={{ fontSize: 10, color: "var(--text3)" }}>{recap ? "Refinanced debt, real estate and platform transaction costs" : "Purchase price allocation, working capital and platform transaction costs"}</div>
            </td>
            <td className="num">{m.fmt$(platformUses)}</td>
          </tr>
          {uSum(addonPriceRows) > 0.5 && (
            <tr data-testid="su-nac-addons"><td>Add-on acquisitions<div style={{ fontSize: 10, color: "var(--text3)" }}>Committed and self-funded add-ons at {m.fmtX(m.rollupModelFor(V).blendedEntry)} blended entry</div></td><td className="num">{m.fmt$(uSum(addonPriceRows))}</td></tr>
          )}
          {uSum(addonNwcRows) > 0.5 && (
            <tr data-testid="su-nac-addon-nwc"><td>Add-on working capital funded at close</td><td className="num">{m.fmt$(uSum(addonNwcRows))}</td></tr>
          )}
          {uSum(addonFeeRows) > 0.5 && (
            <tr data-testid="su-nac-addon-fees"><td>Add-on transaction fees</td><td className="num">{m.fmt$(uSum(addonFeeRows))}</td></tr>
          )}
          {cashRows.map((u, i) => (
            <tr key={u.d} data-testid={`su-cap-cash-${i}`}><td>{u.d}</td><td className="num">{m.fmt$(u.a)}</td></tr>
          ))}
          <tr className="total" data-testid="su-nac-total">
            <td>Total uses{consolidated ? " — consolidated" : ""}</td><td className="num">{m.fmt$(su.totalUses)}</td>
          </tr>
          <tr data-testid="su-nac-memo">
            <td>Memo · net acquisition cost{consolidated ? " — platform" : ""}
              <div style={{ fontSize: 10, color: "var(--text3)" }}>Ask price {V.slbOn ? "− sale-leaseback proceeds " : ""}+ platform transaction costs</div>
            </td>
            <td className="num">{m.fmt$(m.nac(V))}</td>
          </tr>
          <tr><td>Annual debt service</td><td className="num exp">{m.fmt$(-m.totalAnnualDS(V))}</td></tr>
          <tr><td>Cash flow available for debt service</td><td className="num">{m.fmt$(m.cfads(V))}</td></tr>
          <tr className="hl"><td><b>Free cash flow — Year 1</b></td><td className={`num ${m.fcfYr1(V) >= 0 ? "pos" : "neg"}`}><b>{m.fmt$(m.fcfYr1(V))}</b></td></tr>
        </tbody>
      </table>
    </Card>
  );
}
